import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import { GLEntryPreview } from '../services/glCommissionService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { format } from 'date-fns';

interface CommissionGLEntryPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entries: GLEntryPreview[];
  month: number;
  year: number;
  isSubmitting: boolean;
}

const CommissionGLEntryPreviewDialog: React.FC<CommissionGLEntryPreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  entries,
  month,
  year,
  isSubmitting
}) => {
  // Calculate totals
  const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
  const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;
  
  // Format month name
  const monthName = format(new Date(year, month, 1), 'MMMM yyyy');
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>GL Entry Preview</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          The following GL entries will be created for the commission in {monthName}:
        </Typography>
        
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account Number</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell align="right">Debit (RM)</TableCell>
                <TableCell align="right">Credit (RM)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.accountNumber}</TableCell>
                  <TableCell>{entry.accountName}</TableCell>
                  <TableCell align="right">{entry.debit.toFixed(2)}</TableCell>
                  <TableCell align="right">{entry.credit.toFixed(2)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2}><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{totalDebits.toFixed(2)}</strong></TableCell>
                <TableCell align="right"><strong>{totalCredits.toFixed(2)}</strong></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        {isBalanced && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, color: 'success.main' }}>
            <CheckCircleOutlineIcon sx={{ mr: 1 }} />
            <Typography variant="body2">
              Entries are balanced (Total Debits = Total Credits)
            </Typography>
          </Box>
        )}
        
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Note: These entries will be posted to the General Ledger when you confirm. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="primary" 
          variant="contained"
          disabled={isSubmitting || !isBalanced}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Posting...' : 'Confirm & Post to GL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionGLEntryPreviewDialog;
